import React, {
  type ChangeEvent,
  type FC,
  useEffect,
  useRef,
  useState,
} from "react";
import { IoIosSearch } from "react-icons/io";
import { IoCloseCircle } from "react-icons/io5";
import { useTranslation } from "gatsby-plugin-react-i18next";

import Loader from "@components/loader";
import Modal from "@components/modal";
import { composeRefs, useListKeyboardNav } from "@hooks/useListKeyboardNav";
import { type default as CurrencyType } from "@interfaces/Currency";
import { useConfigurationState } from "@stores/configuration";
import { getCurrency } from "@utilities/currencies";
import { cn, defaultRipple, primaryRipple } from "@utilities/helper";
import { markCurrencyChosenByVisitor } from "@utilities/paymentConfiguration";
import { motion } from "motion/react";

const CurrenciesModal: FC<{
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
}> = ({ isModalOpen, setIsModalOpen }) => {
  const { t } = useTranslation();
  const configurationState = useConfigurationState();

  const [currentState, setCurrentState] = useState<{
    query: string;
    currencies: CurrencyType[];
  }>({
    query: "",
    currencies: configurationState.currencies ?? [],
  });

  const resetState = (): void => {
    setCurrentState({
      query: "",
      currencies: configurationState.currencies ?? [],
    });
  };

  const filterCurrencies = (event: ChangeEvent<HTMLInputElement>): void => {
    const filteredQuery = event.target.value;
    if (filteredQuery) {
      const filteredCurrencies: CurrencyType[] =
        configurationState.currencies.filter(currency => {
          return (
            currency.currency
              .toLowerCase()
              .includes(filteredQuery.toLowerCase()) ||
            currency.name.toLowerCase().includes(filteredQuery.toLowerCase()) ||
            currency.symbol
              ?.toLowerCase()
              ?.includes(filteredQuery.toLowerCase())
          );
        });

      setCurrentState({
        query: filteredQuery,
        currencies: filteredCurrencies,
      });
    } else {
      resetState();
    }
  };

  useEffect(() => {
    if (configurationState.currencies.length > 0) {
      if (currentState.currencies.length === 0) {
        setCurrentState({
          query: "",
          currencies: configurationState.currencies,
        });
      }
      const currency = getCurrency(configurationState.currency);
      if (!currency?.conversion_rate) {
        configurationState.resetCurrency();
      }
    }
  }, [configurationState.currency, configurationState.currencies]);
  useEffect(() => {
    resetState();
  }, [isModalOpen]);

  const updateCurrency = (value: CurrencyType): void => {
    markCurrencyChosenByVisitor();
    configurationState.updateCurrency(value.currency);
    setIsModalOpen(false);
  };

  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isModalOpen && configurationState.currencies.length > 0) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isModalOpen, configurationState.currencies.length]);

  const { highlightedIndex, setHighlightedIndex, handleKeyDown, registerItem } =
    useListKeyboardNav<CurrencyType>({
      items: currentState.currencies,
      isActive: isModalOpen,
      onSelect: updateCurrency,
      onEscape: () => {
        setIsModalOpen(false);
      },
    });

  return (
    <Modal
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      isCloseButtonExist={true}
      title={t("choose_currency") ?? ""}
    >
      <div className="flex h-full flex-col gap-2">
        {configurationState.currencies.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <Loader />
          </div>
        ) : (
          <>
            <div className="relative">
              <label>
                <IoIosSearch className="absolute left-[14px] right-auto top-[14px] z-[1] size-[22px] text-gray-400 rtl:left-auto rtl:right-[14px] rtl:-scale-x-100" />
                <input
                  ref={searchInputRef}
                  placeholder={t("search") ?? ""}
                  autoComplete="on"
                  autoCorrect="on"
                  className="custom-form-control block w-full !rounded-3xl !py-3 !pe-5 !ps-12"
                  type="text"
                  aria-label="Search"
                  value={currentState.query}
                  onChange={event => {
                    filterCurrencies(event);
                    setHighlightedIndex(0);
                  }}
                  onKeyDown={handleKeyDown}
                ></input>
                {!!currentState.query && (
                  <motion.button
                    ref={primaryRipple}
                    onClick={resetState}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="focus:custom-primary-focus-ring group absolute left-auto right-[14px] top-[14px] z-[1] flex items-center justify-center rounded-full !border-0 outline-none transition duration-300 hover:!opacity-60 rtl:left-[14px] rtl:right-auto"
                  >
                    <IoCloseCircle className="size-[22px] text-gray-400 transition duration-300 group-hover:text-primary-400" />
                  </motion.button>
                )}
              </label>
            </div>
            <div
              className="-mx-2 -mb-4 flex max-h-[55dvh] flex-col gap-3 overflow-y-auto rounded-2xl px-2 pb-4 pt-2 md:-mx-4 md:-mb-8 md:max-h-[65dvh] md:px-4 md:pb-8"
              role="listbox"
            >
              {currentState.currencies.length === 0 && (
                <div
                  className="flex w-full items-center justify-center py-6 text-sm text-gray-500"
                  role="status"
                >
                  {t("no_results_found")}
                </div>
              )}
              {currentState.currencies.map((currency, index) => {
                const isSelected =
                  configurationState.currency === currency.currency;
                const isHighlighted = index === highlightedIndex;
                return (
                  <motion.button
                    ref={composeRefs<HTMLButtonElement>(
                      defaultRipple,
                      registerItem(index),
                    )}
                    role="option"
                    aria-selected={isSelected}
                    key={"currency_" + currency.id}
                    data-conversion-rate={currency.conversion_rate}
                    onClick={() => {
                      updateCurrency(currency);
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className={cn(
                      "space-between focus:custom-primary-focus-ring flex w-full rounded-3xl !border-2 border-solid border-primary-200 p-3 shadow-lg shadow-gray-300 transition duration-300",
                      {
                        "bg-primary text-white": isSelected,
                        "bg-white hover:text-primary hover:opacity-80":
                          !isSelected,
                        "ring-2 ring-primary/40 ring-offset-1":
                          isHighlighted && !isSelected,
                      },
                    )}
                  >
                    {currency.name}
                  </motion.button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CurrenciesModal;
